import type { ChangeEvent, ReactNode } from "react"

import { cx } from "../../lib/cx"
import { Skeleton } from "../../ui"
import "../screen-tokens.css"
import styles from "./directories.module.css"

/**
 * Каркас экрана справочника за стойкой.
 *
 * Здесь собрано ровно то, что повторяется во всех четырёх справочниках: шапка 46 px,
 * поиск, центрированное состояние и каркас таблицы. Вынесено не ради экономии строк,
 * а ради одного: ЧЕТЫРЕ РАЗДЕЛА ДОЛЖНЫ ВЕСТИ СЕБЯ ОДИНАКОВО. Администратор переходит
 * между ними десятки раз за смену, и шапка, которая в каждом разделе на пару пикселей
 * другой высоты, заставляет глаз перепривыкать на каждом переходе.
 *
 * ТАБЛИЦА ЖИВЁТ ЗДЕСЬ, А НЕ В БИБЛИОТЕКЕ ЭЛЕМЕНТОВ, НАМЕРЕННО. В кабинете таблица одна —
 * список клиентов, и вторая (визиты) устроена иначе. Заводить общий компонент на один
 * случай — значит угадывать, каким он должен быть для второго; когда второй появится,
 * станет видно, что у них общего, и общее переедет в библиотеку.
 */

export function StandHeader(props: {
  readonly title: string
  readonly count?: string | undefined
  readonly children?: ReactNode
  /**
   * Заголовок повторяет имя раздела, которое уже стоит в шапке кабинета («Клиенты»
   * под «Клиенты»). На телефоне такой повтор отнимал строку у списка, и там он
   * остаётся только для чтения голосом. Страница со СВОИМ именем («Правила записи»
   * в разделе «Настройки», «Мастер выбывает» в «Сотрудниках») этот флаг не ставит:
   * её имя в шапке кабинета не написано, и спрятать его — значит оставить человека
   * без ответа, где он.
   */
  readonly repeatsSection?: boolean | undefined
}): ReactNode {
  const repeats = props.repeatsSection === true
  const bare = repeats && (props.count === undefined || props.count.length === 0) && props.children === undefined
  return (
    <header className={cx(styles.header, bare && styles.headerBare)}>
      <h1 className={cx(styles.title, repeats && styles.titleRepeat)}>{props.title}</h1>
      {props.count === undefined || props.count.length === 0 ? null : (
        /*
         * Счётчик объявлен вежливой живой областью: он меняется при каждом поиске,
         * и человеку, слушающему экран, важно услышать «Найдено 143», а не остаться
         * с тишиной после ввода запроса.
         */
        <span className={styles.count} role="status">
          {props.count}
        </span>
      )}
      {props.children === undefined ? null : (
        <div className={styles.headerActions}>{props.children}</div>
      )}
    </header>
  )
}

export function SearchField(props: {
  readonly value: string
  readonly placeholder: string
  readonly label: string
  readonly onChange: (value: string) => void
  readonly className?: string | undefined
}): ReactNode {
  return (
    <div className={cx(styles.search, props.className)}>
      <svg
        className={styles.searchIcon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="6.5" />
        <path d="m20 20-3.8-3.8" />
      </svg>
      <input
        type="search"
        className={styles.input}
        aria-label={props.label}
        placeholder={props.placeholder}
        value={props.value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          props.onChange(event.target.value)
        }}
      />
    </div>
  )
}

/**
 * Центрированное состояние: пусто, отказ, ошибка.
 *
 * Заголовок — настоящий h2, а не крупный текст: по заголовкам ходят клавишами
 * программы чтения с экрана, и пустой экран без заголовка в этом обходе отсутствует.
 */
export function CenterState(props: {
  readonly title: string
  readonly text?: string | undefined
  readonly note?: string | undefined
  readonly actions?: ReactNode
  readonly stacked?: boolean
}): ReactNode {
  return (
    <div className={styles.centerState}>
      <div className={styles.centerCard}>
        <h2 className={styles.centerTitle}>{props.title}</h2>
        {props.text === undefined ? null : <p className={styles.centerText}>{props.text}</p>}
        {props.actions === undefined ? null : (
          <div className={props.stacked === true ? styles.stackedActions : styles.centerActions}>
            {props.actions}
          </div>
        )}
        {props.note === undefined ? null : <p className={styles.centerNote}>{props.note}</p>}
      </div>
    </div>
  )
}

/** Ширины плашек каркаса. Разные намеренно: одинаковые читаются как плита, а не как список. */
const SKELETON_WIDTHS: readonly number[] = [140, 180, 120, 200, 150, 110, 170, 130]

/** Каркас таблицы клиентов: восемь строк, высота строки та же, что у настоящей. */
export function TableSkeleton(props: { readonly columns: number }): ReactNode {
  return (
    <tbody>
      {SKELETON_WIDTHS.map((width, row) => (
        <tr key={`${String(width)}-${String(row)}`}>
          {Array.from({ length: props.columns }, (_, column) => (
            <td key={column}>
              {column === 1 ? (
                <Skeleton width={width} height={11} radius={3} />
              ) : column === 2 ? (
                <Skeleton width={120} height={11} radius={3} />
              ) : column === 4 ? (
                <Skeleton width={70} height={11} radius={3} />
              ) : null}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
}

/** Врезка-пояснение с полосой слева: предупреждение графика, правило раздела. */
export function Note(props: { readonly strong: string; readonly children?: ReactNode }): ReactNode {
  return (
    <div className={styles.note}>
      <span className={styles.noteStrong}>{props.strong}</span>
      {props.children === undefined ? null : <> {props.children}</>}
    </div>
  )
}
